import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { MdLocationOn, MdSave } from "react-icons/md";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl } from "../../../contexts/ContextProvider";
import { Loader, ScrollToTop } from "../../../components";

const UbahDaftarPermohonan = () => {
  const { user, dispatch } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [entryPermohonan, setEntryPermohonan] = useState(null);
  const [status, setStatus] = useState("");
  const [alasan, setAlasan] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getEntryPermohonan();
  }, [id]);

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      dispatch({ type: "LOGOUT" });
      navigate("/");
    }
  };

  const getEntryPermohonan = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${tempUrl}/permohonans/${id}`, {
        id: user._id,
        token: user.token,
      });
      setEntryPermohonan(response.data);
      setStatus(response.data.status === "BELUM" ? "" : response.data.status || "");
      setAlasan(response.data.alasan || "");
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data permohonan.");
    }
    setLoading(false);
  };

  const saveKeputusan = async (event) => {
    event.preventDefault();

    if (!status || !alasan) {
      setError("Status dan alasan wajib diisi.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${tempUrl}/saveKeputusanPermohonan/${id}`, {
        status,
        alasan,
        id: user._id,
        token: user.token,
      });
      navigate(`/namaPemohon/${id}`);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal menyimpan keputusan permohonan.");
      setLoading(false);
    }
  };

  const getPhotoUrl = (photo) => {
    return typeof photo === "string" ? photo : photo?.url;
  };

  const formatCapturedAt = (capturedAt) => {
    if (!capturedAt) return "-";

    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(capturedAt));
  };

  const getGoogleMapsUrl = (photo) => {
    return `https://www.google.com/maps?q=${photo.latitude},${photo.longitude}`;
  };

  const fotoSections = entryPermohonan
    ? [
        { title: "Foto KTP", data: entryPermohonan.fotoKtp || [] },
        { title: "KK", data: entryPermohonan.fotoKk || [] },
        { title: "Penjamin", data: entryPermohonan.fotoPenjamin || [] },
        {
          title: "Kendaraan Depan/Belakang",
          data: entryPermohonan.fotoKendaraan || [],
        },
        { title: "STNK & BPKB", data: entryPermohonan.fotoStnkBpkb || [] },
        { title: "Lingkungan", data: entryPermohonan.fotoLingkungan || [] },
        {
          title: "Lain2 Pendukung",
          data: entryPermohonan.fotoLainPendukung || [],
        },
      ]
    : [];

  const renderFotoGroup = (section) => {
    return (
      <section className="section-panel mt-3" key={section.title}>
        <h2 className="h6 fw-bold mb-3">{section.title}</h2>
        {section.data.length === 0 && <div className="text-muted">Belum ada foto.</div>}
        <div className="photo-grid">
          {section.data.map((photo, index) => (
            <div className="photo-card" key={`${section.title}-${index}`}>
              <button
                type="button"
                onClick={() => window.open(getPhotoUrl(photo), "_blank", "noopener,noreferrer")}
              >
                <img src={getPhotoUrl(photo)} alt={`${section.title} ${index + 1}`} />
                <span>No. {index + 1}</span>
              </button>
              <div className="photo-meta">
                <div>
                  <strong>Waktu</strong>
                  <span>{formatCapturedAt(photo?.capturedAt)}</span>
                </div>
              </div>
              {typeof photo !== "string" &&
                photo?.latitude != null &&
                photo?.longitude != null && (
                  <Button
                    className="photo-map-btn"
                    size="sm"
                    variant="outline-primary"
                    type="button"
                    onClick={() =>
                      window.open(getGoogleMapsUrl(photo), "_blank", "noopener,noreferrer")
                    }
                  >
                    <MdLocationOn /> Lihat Maps
                  </Button>
                )}
            </div>
          ))}
        </div>
      </section>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Survey</div>
          <h1 className="page-title">Ubah Daftar Permohonan</h1>
        </div>
      </div>
      <section className="section-panel">
        {error && <Alert variant="warning">{error}</Alert>}
        <Form onSubmit={saveKeputusan}>
          <Row>
            <Col lg={6} md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Nama Pemohon</Form.Label>
                <Form.Control value={entryPermohonan?.namaPemohon || ""} disabled />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Surveyor</Form.Label>
                <Form.Control
                  value={
                    entryPermohonan?.userId?.kodeSurveyor?.kodeSurveyor
                      ? `${entryPermohonan.userId.kodeSurveyor.kodeSurveyor} - ${entryPermohonan.userId.kodeSurveyor.namaSurveyor}`
                      : entryPermohonan?.userId?.username || ""
                  }
                  disabled
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">PILIH STATUS</option>
                  <option value="ACC">ACC</option>
                  <option value="REJECT">REJECT</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Alasan</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={alasan}
                  onChange={(event) => setAlasan(event.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="form-actions mt-3">
            <Button
              className="btn-back"
              type="button"
              variant="outline-secondary"
              onClick={() => navigate(`/namaPemohon/${id}`)}
            >
              {"< Kembali"}
            </Button>
            <Button className="btn-icon" type="submit">
              <MdSave /> Simpan
            </Button>
          </div>
        </Form>
      </section>
      {fotoSections.map(renderFotoGroup)}
    </div>
  );
};

export default UbahDaftarPermohonan;
